import React from "react";
import { UserCheck, UserX, UserMinus, Users } from "lucide-react";
import { UserStats } from "../../../lib/api/dashboard";

interface UserStatusBreakdownProps {
  data?: UserStats;
  loading?: boolean;
}

const UserStatusBreakdown: React.FC<UserStatusBreakdownProps> = ({
  data,
  loading,
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const total = data?.total || 1;

  const statuses = [
    {
      label: "Active Users",
      value: data?.active ?? 0,
      percent: Math.round(((data?.active ?? 0) / total) * 100),
      icon: UserCheck,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      barColor: "bg-emerald-500",
    },
    {
      label: "Suspended",
      value: data?.suspended ?? 0,
      percent: Math.round(((data?.suspended ?? 0) / total) * 100),
      icon: UserMinus,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      barColor: "bg-amber-500",
    },
    {
      label: "Terminated",
      value: data?.terminated ?? 0,
      percent: Math.round(((data?.terminated ?? 0) / total) * 100),
      icon: UserX,
      color: "text-red-600",
      bgColor: "bg-red-50",
      barColor: "bg-red-500",
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">User Status</h3>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <Users className="w-4 h-4" />
          <span>{data?.total ?? 0}</span>
        </div>
      </div>

      {/* New users highlight */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">New This Month</p>
            <p className="text-2xl font-bold text-gray-900">
              {data?.newThisMonth ?? 0}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">This Week</p>
            <p className="text-lg font-semibold text-blue-600">
              +{data?.newThisWeek ?? 0}
            </p>
          </div>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="space-y-3">
        {statuses.map((status) => (
          <div
            key={status.label}
            className={`${status.bgColor} rounded-lg p-3`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <status.icon className={`w-4 h-4 ${status.color}`} />
                <span className="text-sm font-medium text-gray-700">
                  {status.label}
                </span>
              </div>
              <span className={`text-sm font-semibold ${status.color}`}>
                {status.value}
              </span>
            </div>
            <div className="h-1.5 w-full bg-white/50 rounded-full overflow-hidden">
              <div
                className={`h-full ${status.barColor} rounded-full transition-all duration-500`}
                style={{ width: `${status.percent}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Profile completion */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Profile Completion Rate</span>
          <span className="text-sm font-semibold text-gray-900">
            {data?.profileCompletionRate ?? 0}%
          </span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
            style={{ width: `${data?.profileCompletionRate ?? 0}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default UserStatusBreakdown;
